import { FileAudio, FileText, Plus, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { useApp } from "@/lib/AppContext";
import { formatDate } from "@/lib/format";
import { addedName, PIPELINE_STATE, useAddSource } from "@/lib/useAddSource";

/**
 * UI-16's **Add a source** — the whole of capture on one page.
 *
 * The same departure the phone app makes and for the same reason: full mode has a route per
 * kind because somebody who knows the difference between a recording and a contract can
 * pick the right door, and this surface's reader does not have to. One drop zone, and the
 * file's own extension picks the route (`useAddSource`), so what is accepted here and what
 * is accepted on a phone are one list and one refusal sentence.
 *
 * The pipeline settings full mode's audio route carries — spoken language, expected
 * speakers — are not asked for and not guessed at. "Decide for me" is a real answer the
 * Server understands, and it is a better one than a number typed by somebody who has never
 * been told what diarization is.
 */
export function SimpleAdd() {
  const { recentSources, recordings } = useApp();
  const add = useAddSource();

  const picker = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  const uploading = add.progress !== null;

  return (
    <div className="mx-auto flex max-w-[760px] flex-col gap-[var(--space-6)] px-[var(--space-8)] py-[var(--space-12)]">
      <header className="flex flex-col gap-2.5">
        <h1 className="text-[40px] font-extrabold leading-[1.1] tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
          Add a source
        </h1>
        <p className="max-w-[54ch] text-[length:var(--text-lg)] leading-[1.6] text-[var(--text-secondary)] [text-wrap:pretty]">
          Audio, documents or a note. Factorium reads it and updates your world view.
        </p>
      </header>

      <div
        onDragOver={(event) => {
          event.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setOver(false);
          add.send(event.dataTransfer.files.item(0));
        }}
        className="flex flex-col items-center gap-[var(--space-4)] rounded-[var(--radius-xl)] border border-dashed bg-[var(--surface-glass)] px-[var(--space-6)] py-[var(--space-12)] text-center backdrop-blur-[var(--blur-sm)] transition-colors"
        style={{ borderColor: over ? "var(--vl-life)" : "var(--border-default)" }}
      >
        <div className="flex items-center gap-[var(--space-4)] text-[var(--text-muted)]">
          <FileAudio size={24} strokeWidth={2} aria-hidden />
          <FileText size={24} strokeWidth={2} aria-hidden />
        </div>

        <p className="text-[length:var(--text-lg)] font-semibold text-[var(--text-primary)]">Drop files here</p>
        <p className="max-w-[42ch] text-[length:var(--text-sm)] text-[var(--text-muted)] [text-wrap:pretty]">
          Meetings, calls, contracts, notes.
        </p>

        <input
          ref={picker}
          type="file"
          className="hidden"
          accept={add.accepted.join(",")}
          onChange={(event) => {
            const chosen = event.target.files?.[0] ?? null;
            // Cleared so choosing the same file twice fires a second change event, which
            // is the only way a retry after a failure works.
            event.target.value = "";
            add.send(chosen);
          }}
        />

        <button
          type="button"
          disabled={uploading}
          onClick={() => picker.current?.click()}
          className="mt-1 inline-flex items-center gap-1.5 rounded-[var(--radius-md)] bg-[var(--primary)] px-4 py-1.5 text-[length:var(--text-sm)] font-semibold text-[var(--primary-foreground)] transition hover:brightness-110 disabled:opacity-50"
        >
          <Upload size={14} strokeWidth={2.5} />
          {uploading ? `Uploading… ${Math.round((add.progress ?? 0) * 100)}%` : "Choose files"}
        </button>

        {uploading && (
          <div className="flex w-full max-w-[420px] flex-col items-center gap-2">
            <div className="h-1 w-full overflow-hidden rounded-[var(--radius-full)] bg-[var(--surface-2)]">
              <div
                className="h-full bg-[var(--vl-bridge)] transition-[width]"
                style={{ width: `${Math.round((add.progress ?? 0) * 100)}%` }}
              />
            </div>
            <button
              type="button"
              onClick={add.cancel}
              className="text-[length:var(--text-xs)] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {add.added && (
        <p className="rounded-[var(--radius-md)] border border-[var(--success)] bg-[color-mix(in_oklch,var(--success)_12%,transparent)] p-[var(--space-3)] text-[length:var(--text-sm)] text-[var(--text-secondary)]">
          {addedName(add.added) ? (
            <>
              <span className="font-semibold text-[var(--text-primary)]">{addedName(add.added)}</span> is in.
            </>
          ) : (
            "That is in."
          )}{" "}
          It appears below once it has been read.
        </p>
      )}

      {/* CAP-3. The original is named rather than linked: where it opens is a full-mode
          screen, and a link out of this surface into one is worse than a sentence saying
          which file it already has. */}
      {add.duplicate && (
        <p className="rounded-[var(--radius-md)] border border-[var(--warning)] bg-[color-mix(in_oklch,var(--warning)_12%,transparent)] p-[var(--space-3)] text-[length:var(--text-sm)] text-[var(--text-secondary)]">
          You already added this file
          {add.duplicate.title ? ` as “${add.duplicate.title}”` : ""} on {formatDate(add.duplicate.uploadedAt)}.
          Nothing was uploaded twice.
        </p>
      )}

      {add.problem && (
        <p role="alert" className="text-[length:var(--text-sm)] text-[var(--warning)] [text-wrap:pretty]">
          {add.problem}
        </p>
      )}

      {/* CAP-7 — a note is a source like any other, kept and read the same way. */}
      <div className="flex flex-col gap-2.5">
        <p className="text-[length:var(--text-2xs)] uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
          Or write it down
        </p>
        <textarea
          value={add.note}
          rows={4}
          aria-label="Write a note"
          placeholder="What happened, what was decided, what you want to remember…"
          onChange={(event) => add.setNote(event.target.value)}
          className="w-full resize-y rounded-[var(--radius-md)] border border-[var(--input)] bg-[var(--surface-glass)] p-[var(--space-3)] text-[length:var(--text-md)] leading-[1.55] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-muted)] focus:border-[var(--vl-bridge)]"
        />
        <div className="flex justify-end">
          <button
            type="button"
            disabled={add.saving || add.note.trim().length === 0}
            onClick={add.saveNote}
            className="inline-flex items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-1.5 text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)] transition hover:bg-[var(--surface-2)] disabled:opacity-50"
          >
            <Plus size={14} strokeWidth={2.5} />
            {add.saving ? "Saving…" : "Add note"}
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-2.5 pt-[var(--space-2)]">
        <p className="text-[length:var(--text-2xs)] uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
          Recently added
        </p>

        {recentSources.length === 0 ? (
          <p className="text-[length:var(--text-sm)] text-[var(--text-muted)]">Nothing yet.</p>
        ) : (
          recentSources.map((source) => {
            const recording = recordings.find((candidate) => candidate.id === source.id);
            const state = recording ? PIPELINE_STATE[recording.status ?? "queued"] : null;

            return (
              <div
                key={source.id}
                className="flex items-center gap-2.5 border-b border-[var(--border-subtle)] py-2.5 text-[length:var(--text-sm)]"
              >
                <span
                  aria-hidden
                  className="size-1.5 shrink-0 rounded-full"
                  style={{ background: state?.dot ?? "var(--text-muted)" }}
                />
                <span className="min-w-0 flex-1 truncate text-[var(--text-primary)]">
                  {source.title ?? source.originalFilename ?? "Untitled source"}
                </span>
                <span className="shrink-0 text-[length:var(--text-2xs)] text-[var(--text-muted)]">
                  {formatDate(source.sourceDate ?? source.createdAt)}
                </span>
                {/* A recording has a pipeline state worth reporting. A document or a note
                    has no run on this list, so the column is left blank rather than filled
                    with a state invented for it. */}
                <span className="w-[120px] shrink-0 text-right text-[length:var(--text-2xs)] text-[var(--text-muted)]">
                  {state?.label ?? ""}
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
